import React, { useState, useEffect } from 'react';
import {
    IStackStyles,
    Label,
    MessageBar,
    MessageBarType,
    SearchBox,
    Spinner,
    SpinnerSize,
    Stack,
} from '@fluentui/react';
import { Checkbox } from './checkbox';
import {
    MemberResolutionStatus,
    ResolvedChatMember,
} from '../../store/reducers/conversation/reducer';
import { useAppSelector } from '../../store';
import { selectTranslate } from '../../store/selectors/ui';

// Member search is toggled if group member count exceeds this threshold
const SEARCH_THRESHOLD = 5;

const memberListStyles: IStackStyles = {
    root: {
        maxHeight: 240,
        overflowY: 'auto',
        border: '1px solid #EDEBE9',
        borderRadius: 2,
        padding: 8,
    },
};

export interface ChatMemberSelectorProps {
    members: ResolvedChatMember[];
    status: MemberResolutionStatus;
    selectedUserIds: string[];
    onSelectionChange: (selectedMembers: ResolvedChatMember[]) => void;
    searchDebounceMs?: number;
}

export const ChatMemberSelector: React.FC<ChatMemberSelectorProps> = ({
    members,
    status,
    selectedUserIds,
    onSelectionChange,
    searchDebounceMs = 250,
}) => {
    const translate = useAppSelector(selectTranslate);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(
            () => setDebouncedSearch(search),
            searchDebounceMs,
        );
        return () => clearTimeout(timer);
    }, [search, searchDebounceMs]);

    if (status !== MemberResolutionStatus.Done) {
        return (
            <Spinner
                size={SpinnerSize.medium}
                label={translate('resolving_members')}
            />
        );
    }

    const hasResolvableMembers = members.some((member) => member.ncShareWith);
    if (!hasResolvableMembers) {
        return (
            <MessageBar
                messageBarType={MessageBarType.warning}
                isMultiline={true}
            >
                {translate('no_members_with_account')}
            </MessageBar>
        );
    }

    const filteredMembers = members.filter((member) =>
        member.displayName
            .toLowerCase()
            .includes(debouncedSearch.toLowerCase()),
    );

    const onMemberToggle = (member: ResolvedChatMember, checked: boolean) => {
        const newSelectedMembers = checked
            ? [
                  ...members.filter((m) => selectedUserIds.includes(m.userId)),
                  member,
              ]
            : members.filter(
                  (m) =>
                      selectedUserIds.includes(m.userId) &&
                      m.userId !== member.userId,
              );
        onSelectionChange(newSelectedMembers);
    };

    const getMemberLabel = (member: ResolvedChatMember): string => {
        if (member.ncShareWith) {
            return member.displayName;
        }
        const hint = member.resolveError
            ? translate('member_lookup_failed')
            : translate('member_no_account');
        return `${member.displayName} (${hint})`;
    };

    return (
        <Stack tokens={{ childrenGap: 4 }}>
            <Label>{translate('chat_members_label')}</Label>
            {members.length > SEARCH_THRESHOLD && (
                <SearchBox
                    placeholder={translate('search')}
                    onChange={(_ev, value) => setSearch(value ?? '')}
                    onClear={() => setSearch('')}
                />
            )}
            <Stack tokens={{ childrenGap: 8 }} styles={memberListStyles}>
                {filteredMembers.map((member) => (
                    <Checkbox
                        key={member.userId}
                        color="var(--sdt-primary)"
                        checked={selectedUserIds.includes(member.userId)}
                        disabled={!member.ncShareWith}
                        label={getMemberLabel(member)}
                        onChange={(_ev, checked) =>
                            onMemberToggle(member, Boolean(checked))
                        }
                    />
                ))}
            </Stack>
        </Stack>
    );
};
